/**
 * Firestore シリアライズ規約 (§2.5) の実装。
 *
 * - ジオメトリ (座標のネスト配列) は Firestore に保存できないため JSON 文字列化。
 *   文字列化の単位はフィールド値全体。キー名は変えない。
 * - undefined キーは除去 (SDK は undefined で即例外。ignoreUndefinedProperties は
 *   追加しない — serializer のパス漏れを例外で顕在化させる安全弁 §2.5)。
 * - null は保持 (smoking buildingPolygon の3値 §2.2)。
 * - Timestamp → ISO 文字列 (§2.4。一覧ソート localeCompare / persisted-crud の
 *   updatedAt: string 型制約との整合)。
 */

export type CollectionKind =
  | 'properties_v1'
  | 'properties_v2'
  | 'smoking_properties'
  | 'knowledge_records'
  | 'building_master';

/** §2.5 のジオメトリ一覧。パス構文: 'a' / 'a.b' / 'a[].b' (配列要素の子)。 */
export const GEOMETRY_PATHS: Record<CollectionKind, string[]> = {
  properties_v1: [
    'footprint',
    'evacuation.polygon',
    'evacuation.routePolyline',
    'labels[].shape',
    // 候補は routes[].coordinates が二重ネストのためフィールド丸ごと文字列化
    'routeCandidates',
  ],
  properties_v2: [
    'footprint',
    'destinations.evacuationArea.polygon',
    'destinations.evacuationArea.routePolyline',
    'destinations.shelter.polygon',
    'destinations.shelter.routePolyline',
    'labels[].shape',
    'routeCandidates',
  ],
  smoking_properties: ['buildingPolygon'],
  // ナレッジレコードは座標のネスト配列を持たない (スカラー/文字列/平坦配列のみ)
  knowledge_records: [],
  // 物件マスタは coords を持つが {lng, lat} の平坦なオブジェクトなので変換不要。
  // ここに並べるのは Firestore が保存できない**ネスト配列**だけ。
  building_master: [],
};

type Dict = Record<string, unknown>;

function isTimestampLike(v: unknown): v is { toDate: () => Date } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { toDate?: unknown }).toDate === 'function'
  );
}

/** パスに従い value を変換する (encode/decode 共用の walker)。 */
function transformAtPath(
  obj: Dict,
  path: string[],
  fn: (v: unknown) => unknown,
): void {
  const [head, ...rest] = path;
  if (!head) return;
  if (head.endsWith('[]')) {
    const arr = obj[head.slice(0, -2)];
    if (!Array.isArray(arr)) return;
    for (const el of arr) {
      if (el && typeof el === 'object') transformAtPath(el as Dict, rest, fn);
    }
    return;
  }
  if (rest.length === 0) {
    if (head in obj && obj[head] !== undefined && obj[head] !== null) {
      obj[head] = fn(obj[head]);
    }
    return;
  }
  const child = obj[head];
  if (child && typeof child === 'object') transformAtPath(child as Dict, rest, fn);
}

/** undefined キーを深く除去 (配列内オブジェクトも)。null は保持。 */
function stripUndefined(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stripUndefined);
  if (v && typeof v === 'object') {
    const out: Dict = {};
    for (const [k, val] of Object.entries(v as Dict)) {
      if (val === undefined) continue;
      out[k] = stripUndefined(val);
    }
    return out;
  }
  return v;
}

/** Timestamp 値を ISO 文字列へ深く変換。 */
function convertTimestamps(v: unknown): unknown {
  if (isTimestampLike(v)) return v.toDate().toISOString();
  if (Array.isArray(v)) return v.map(convertTimestamps);
  if (v && typeof v === 'object') {
    const out: Dict = {};
    for (const [k, val] of Object.entries(v as Dict)) out[k] = convertTimestamps(val);
    return out;
  }
  return v;
}

export function serializeDoc(kind: CollectionKind, obj: Dict): Dict {
  const clone = JSON.parse(JSON.stringify(obj)) as Dict; // undefined は既に落ちる
  delete clone.id;
  for (const p of GEOMETRY_PATHS[kind]) {
    transformAtPath(clone, p.split('.'), (v) => JSON.stringify(v));
  }
  return stripUndefined(clone) as Dict;
}

export function deserializeDoc<T>(kind: CollectionKind, id: string, data: Dict): T {
  const converted = convertTimestamps(data) as Dict;
  for (const p of GEOMETRY_PATHS[kind]) {
    transformAtPath(converted, p.split('.'), (v) =>
      typeof v === 'string' ? (JSON.parse(v) as unknown) : v,
    );
  }
  return { ...converted, id } as T;
}
